import { SettingsGroup, SettingsGroupItem } from '@sb/design-system';
import type { RestoreDifference } from '@sb/feathers-types';

import { RestoreAlert } from './RestoreAlert';

interface RestoreDifferencesPanelProps {
  differences: RestoreDifference[];
}

function RestoreDifferenceRow({
  difference,
}: {
  difference: RestoreDifference;
}) {
  return (
    <SettingsGroupItem
      className="tw-py-8"
      data-testid={`restore-confirmation-difference-${difference.kind}`}
    >
      <div className="tw-flex tw-flex-col tw-flex-1 tw-gap-2 tw-min-w-0">
        <span>{difference.label}</span>

        <span className="tw-text-13 tw-text-label-tertiary tw-truncate">
          Backup{' '}
          <span className="tw-text-label-secondary">
            {difference.checkpointValue}
          </span>
          {' · this robot '}
          <span className="tw-text-label-secondary">
            {difference.localValue}
          </span>
        </span>
      </div>
    </SettingsGroupItem>
  );
}

/**
 * Every way the backup's robot is not this robot.
 *
 * The cloud gate is deliberately coarse — it compares arm MODEL and never
 * SERIAL — so a same-model, different-arm restore reaches the robot with
 * nothing said about it. This panel is where that gets said.
 */
export function RestoreDifferencesPanel({
  differences,
}: RestoreDifferencesPanelProps) {
  if (differences.length === 0) {
    return null;
  }

  const hasBlocking = differences.some((d) => d.severity === 'blocking');

  return (
    <section
      className="tw-flex tw-flex-col tw-gap-8"
      data-testid="restore-confirmation-differences-region"
    >
      <h5 className="tw-heading-40 tw-pl-16">Different from this robot</h5>

      <SettingsGroup>
        {differences.map((difference) => (
          <RestoreDifferenceRow key={difference.kind} difference={difference} />
        ))}
      </SettingsGroup>

      {hasBlocking && (
        <RestoreAlert
          severity="critical"
          data-testid="restore-confirmation-blocking-banner"
        >
          The robot re-checks the arm model before it erases anything and will
          refuse this restore. Order it against a matching robot instead.
        </RestoreAlert>
      )}
    </section>
  );
}
